import React, { useState, useEffect, useMemo } from "react";
import styled from "styled-components";
import { API_ENDPOINTS } from "../config/api";
import { useParams } from "react-router-dom";
import { useNavigateWithCommandClick } from "../hooks/useNavigateWithCommandClick";
import { getBotIcon } from "../utils/botFormatting";

import Button from "../components/Button";
import Modal, {
  ModalTitle,
  ModalDescription,
  ModalActions,
  BotList,
  BotItem,
  BotCheckbox,
  BotLabel,
  BotIcon
} from "../components/Modal";
import PageHeader from "../components/PageHeader";
import PageContent from "../components/PageContent";
import { Card } from "../components/Card";

// Import tab components
import UsersTab from "./admin/UsersTab";
import UsernameRulesTab from "./admin/UsernameRulesTab";
import DomainRulesTab from "./admin/DomainRulesTab";
import ChatLogsTab from "./admin/ChatLogsTab";

import BotsTab from "./admin/BotsTab";
import FeedbackTab from "./admin/FeedbackTab";
import EvalsTab from "./admin/EvalsTab";

const Tabs = styled.div`
  display: flex;
  overflow-x: auto;
  box-shadow: inset 0 -0.125rem 0 0 #e1e5e9;
  position: relative;

  @media (max-width: 768px) {
    flex-wrap: wrap;
  }
`;

const Tab = styled.button`
  background: none;
  border: none;
  padding: 1rem 1.5rem;
  font-size: 1rem;
  font-weight: 600;
  cursor: pointer;

  color: ${(props) => (props.$active ? "#000000" : "rgba(0, 0, 0, 0.5)")};
  white-space: nowrap;
  position: relative;

  border-bottom: 0.125rem solid transparent;
  border-bottom-color: ${(props) =>
    props.$active ? "#000000" : "transparent"};
  ${(props) => props.$active && `position: relative; z-index: 1;`}

  &:hover {
    color: rgba(0, 0, 0, 0.8);
  }

  @media (max-width: 768px) {
    padding: 0.75rem 1rem;
    font-size: 1rem;
    flex: 1;
    min-width: auto;
  }
`;

const TabContent = styled.div`
  padding: 2rem;

  @media (max-width: 768px) {
    padding: 1rem;
  }
`;

const AccessDenied = styled.div`
  text-align: center;
  padding: 4rem 2rem;
  color: #6c757d;

  h2 {
    color: #dc3545;
    margin-bottom: 1rem;
    font-size: 2rem;
  }

  p {
    font-size: 1.1rem;
    margin-bottom: 2rem;
  }

  .icon {
    font-size: 4rem;
    margin-bottom: 1rem;
    color: #dc3545;
  }
`;

const ModeOption = styled.div`
  display: flex;
  align-items: center;
  gap: 0.75rem;
  margin-bottom: 0.75rem;
  padding: 0.75rem;
  background: white;
  border-radius: 6px;
  cursor: pointer;
  border: 2px solid ${(props) => (props.$isActive ? "#007bff" : "#e1e5e9")};
  transition: all 0.3s ease;

  &:hover {
    border-color: ${(props) => (props.$isActive ? "#007bff" : "#ccc")};
    background: ${(props) => (props.$isActive ? "#f0f7ff" : "#fafafa")};
  }

  &:last-child {
    margin-bottom: 0;
  }
`;

const ModeRadio = styled.input`
  margin: 0;
  cursor: pointer;
`;

const ModeLabel = styled.label`
  flex: 1;
  cursor: pointer;
  font-weight: 500;

  small {
    display: block;
    color: #666;
    font-weight: normal;
    margin-top: 0.25rem;
  }
`;

function Admin({ user, onBack }) {
  const { tab, domain, filter, uuidFilter, botId } = useParams();
  const [error, setError] = useState("");
  const [successMessage, setSuccessMessage] = useState("");
  const [availableBots, setAvailableBots] = useState([]);
  const [botSelectionModal, setModal] = useState(null);
  const [selectedBots, setSelectedBots] = useState(new Set());
  const [updatingBots, setUpdatingBots] = useState(false);
  const [loadingUserBots, setLoadingUserBots] = useState(false);
  const [isDomainMode, setIsDomainMode] = useState(false);
  const [isDefaultMode, setIsDefaultMode] = useState(false);
  const [usernameRules, setUsernameRules] = useState([]);
  const [domainRules, setDomainRules] = useState([]);
  const navigate = useNavigateWithCommandClick();

  // Valid tab IDs for validation
  const validTabs = useMemo(() => {
    return [
      "users",
      "bots",
      "chat-logs",
      "username-rules",
      "domain-rules",
      "feedback",
      "evals"
    ];
  }, []);

  // Get active tab from URL, default to "users"
  // Handle /admin/:tab, /admin/chat-logs/:domain, and /admin/bots/:filter patterns
  const activeTab = (() => {
    if (domain) {
      // If we have a domain parameter, we're in chat-logs with domain filter
      return "chat-logs";
    }
    if (filter) {
      // If we have a filter parameter, we're in bots with filter
      return "bots";
    }
    return tab && validTabs.includes(tab) ? tab : "users";
  })();

  // Function to handle tab changes
  const handleTabChange = (tabId, event) => {
    if (validTabs.includes(tabId)) {
      navigate(`/admin/${tabId}`, {}, event);
    }
  };

  const onError = (message) => {
    setError(message);
    setSuccessMessage("");
  };

  const onSuccess = (message) => {
    setSuccessMessage(message);
    setError("");
    setTimeout(() => setSuccessMessage(""), 3000);
  };

  const fetchBots = async (username) => {
    try {
      const url = username
        ? `${API_ENDPOINTS.bots}?username=${encodeURIComponent(username)}`
        : API_ENDPOINTS.bots;
      const response = await fetch(url);
      if (response.ok) {
        const data = await response.json();
        setAvailableBots(data.bots || []);
      }
    } catch (error) {
      console.error("Error fetching bots:", error);
    }
  };

  const fetchUserBots = async (username) => {
    try {
      const response = await fetch(
        `${API_ENDPOINTS.bots}?username=${encodeURIComponent(username)}`
      );
      if (response.ok) {
        const data = await response.json();
        return data.bots || [];
      }
      return [];
    } catch (error) {
      console.error("Error fetching user bots:", error);
      return [];
    }
  };

  const fetchUserAccess = async (userId) => {
    try {
      const token = localStorage.getItem("token");
      const response = await fetch(API_ENDPOINTS.getUserAccess(userId), {
        headers: {
          Authorization: `Bearer ${token}`,
          "Content-Type": "application/json"
        }
      });

      if (response.ok) {
        const data = await response.json();
        return {
          ...data.user,
          accessType: data.accessDetails?.type,
          domain: data.accessDetails?.domain,
          allowedBots: data.accessDetails?.allowedBots || data.user.allowedBots,
          rawAllowedBots: data.user.rawAllowedBots
        };
      } else {
        throw new Error("Failed to fetch user access info");
      }
    } catch (error) {
      console.error("Error fetching user access:", error);
      throw error;
    }
  };

  const handleBotSelectionClick = async (userToEdit) => {
    setLoadingUserBots(true);
    try {
      const updatedUser = await fetchUserAccess(userToEdit.id);
      setModal(updatedUser);

      const isDomainByAllowedBots =
        (updatedUser.rawAllowedBots &&
          Array.isArray(updatedUser.rawAllowedBots) &&
          updatedUser.rawAllowedBots.length === 1 &&
          updatedUser.rawAllowedBots[0] === "domain") ||
        (updatedUser.allowedBots &&
          Array.isArray(updatedUser.allowedBots) &&
          updatedUser.allowedBots.length === 1 &&
          updatedUser.allowedBots[0] === "domain");

      const isDefaultByAllowedBots =
        (updatedUser.rawAllowedBots &&
          Array.isArray(updatedUser.rawAllowedBots) &&
          updatedUser.rawAllowedBots.length === 1 &&
          updatedUser.rawAllowedBots[0] === "default") ||
        (updatedUser.allowedBots &&
          Array.isArray(updatedUser.allowedBots) &&
          updatedUser.allowedBots.length === 1 &&
          updatedUser.allowedBots[0] === "default");

      const isDomainByAccessType = updatedUser.accessType === "domain-based";
      const isDefaultByAccessType = updatedUser.accessType === "default";

      const isDomain = isDomainByAllowedBots || isDomainByAccessType;
      const isDefault = isDefaultByAllowedBots || isDefaultByAccessType;

      setIsDomainMode(isDomain);
      setIsDefaultMode(isDefault);

      if (isDomain || isDefault) {
        setSelectedBots(new Set());
      } else {
        const userBots = await fetchUserBots(updatedUser.username);
        setSelectedBots(new Set(userBots.map((bot) => bot.id)));
      }
    } catch (error) {
      console.error("Error fetching user bots:", error);
      onError("Failed to load user's allowed bots");
    } finally {
      setLoadingUserBots(false);
    }
  };

  // Store reference to handleBotSelectionClick in window for the UsersTab
  window.handleBotSelectionClick = handleBotSelectionClick;

  const fetchUsernameRules = async () => {
    try {
      const token = localStorage.getItem("token");
      const response = await fetch(API_ENDPOINTS.adminUsernameRules, {
        headers: {
          Authorization: `Bearer ${token}`,
          "Content-Type": "application/json"
        }
      });
      const data = await response.json();
      if (response.ok) {
        setUsernameRules(data.rules || data.usernameRules || []);
      }
    } catch (error) {
      console.error("Error fetching username rules:", error);
    }
  };

  const fetchDomainRules = async () => {
    try {
      const token = localStorage.getItem("token");
      const response = await fetch(API_ENDPOINTS.adminDomains, {
        headers: {
          Authorization: `Bearer ${token}`,
          "Content-Type": "application/json"
        }
      });
      const data = await response.json();
      if (response.ok) {
        setDomainRules(data.domains || []);
      }
    } catch (error) {
      console.error("Error fetching domain rules:", error);
    }
  };

  const handleBotToggle = (botId) => {
    setSelectedBots((prev) => {
      const newSet = new Set(prev);
      if (newSet.has(botId)) {
        newSet.delete(botId);
      } else {
        newSet.add(botId);
      }
      return newSet;
    });
  };

  const handleSaveAllowedBots = async () => {
    if (!botSelectionModal) return;

    setUpdatingBots(true);
    onError("");

    try {
      const token = localStorage.getItem("token");
      let response, data;

      if (isDomainMode) {
        response = await fetch(
          API_ENDPOINTS.resetToDomainBots(botSelectionModal.id),
          {
            method: "PUT",
            headers: {
              Authorization: `Bearer ${token}`,
              "Content-Type": "application/json"
            }
          }
        );
      } else if (isDefaultMode) {
        response = await fetch(
          API_ENDPOINTS.resetToDefaultBots(botSelectionModal.id),
          {
            method: "PUT",
            headers: {
              Authorization: `Bearer ${token}`,
              "Content-Type": "application/json"
            }
          }
        );
      } else {
        response = await fetch(
          API_ENDPOINTS.updateAllowedBots(botSelectionModal.id),
          {
            method: "PUT",
            headers: {
              Authorization: `Bearer ${token}`,
              "Content-Type": "application/json"
            },
            body: JSON.stringify({
              allowedBots: Array.from(selectedBots)
            })
          }
        );
      }

      data = await response.json();

      if (response.ok) {
        const successMsg = isDomainMode
          ? "Switched to domain-based bot allowlist"
          : isDefaultMode
          ? "Switched to default bot allowlist"
          : "Allowed bots updated successfully";
        onSuccess(data.message || successMsg);
        setModal(null);
      } else {
        onError(
          data.error ||
            `Failed to ${
              isDomainMode
                ? "switch to domain mode"
                : isDefaultMode
                ? "switch to default mode"
                : "update allowed bots"
            }`
        );
      }
    } catch (error) {
      onError("Network error. Please try again.");
    } finally {
      setUpdatingBots(false);
    }
  };

  const handleCancelBotSelection = () => {
    setModal(null);
    setSelectedBots(new Set());
    setIsDomainMode(false);
    setIsDefaultMode(false);
  };

  // Redirect to default tab if no tab is specified or invalid tab
  // Don't redirect if we have a domain parameter (domain-filtered chat logs) or filter parameter (filtered bots)
  useEffect(() => {
    if (!domain && !filter && (!tab || !validTabs.includes(tab))) {
      navigate("/admin/users", { replace: true });
    }
  }, [tab, domain, filter, navigate, validTabs]);

  useEffect(() => {
    if (user?.isAdmin) {
      fetchBots();
      fetchDomainRules();
      fetchUsernameRules();
    }
  }, [user?.isAdmin]);

  const tabs = useMemo(() => {
    const baseTabs = [
      { id: "users", label: "Users" },
      { id: "bots", label: "Bots" },
      { id: "chat-logs", label: "Chat logs" },
      { id: "domain-rules", label: "Domain rules" },
      { id: "username-rules", label: "Username rules" },
      { id: "feedback", label: "Feedback" },
      { id: "evals", label: "Evals" }
    ];
    return baseTabs;
  }, []);

  const renderTabContent = () => {
    switch (activeTab) {
      case "users":
        return <UsersTab user={user} onError={onError} onSuccess={onSuccess} />;
      case "bots":
        return (
          <BotsTab
            onError={onError}
            onSuccess={onSuccess}
            onBotCreated={() => {
              fetchBots();
              fetchDomainRules();
            }}
          />
        );
      case "username-rules":
        return (
          <UsernameRulesTab
            onError={onError}
            onSuccess={onSuccess}
            availableBots={availableBots}
          />
        );
      case "domain-rules":
        return (
          <DomainRulesTab
            onError={onError}
            onSuccess={onSuccess}
            availableBots={availableBots}
          />
        );
      case "chat-logs":
        return (
          <ChatLogsTab
            onError={onError}
            onSuccess={onSuccess}
            domainFilter={domain}
            uuidFilterParam={uuidFilter}
            botIdParam={botId}
          />
        );
      case "feedback":
        return <FeedbackTab onError={onError} onSuccess={onSuccess} />;
      case "evals":
        return <EvalsTab user={user} onError={onError} onSuccess={onSuccess} />;
      default:
        return null;
    }
  };

  // Check if user is admin
  if (!user?.isAdmin) {
    return (
      <>
        <PageHeader title="Admin" showBackButton={true} onBack={onBack} />
        <PageContent>
          <Card>
            <AccessDenied>
              <div className="icon">🚫</div>
              <h2>Access denied</h2>
              <p>You don't have permission to access the admin panel.</p>
              <p>Only administrators can view and manage users.</p>
              <Button onClick={onBack}>Go Back</Button>
            </AccessDenied>
          </Card>
        </PageContent>
      </>
    );
  }

  return (
    <>
      <PageHeader
        title="Admin"
        showBackButton={true}
        onBack={onBack}
        successMessage={successMessage}
        errorMessage={error}
      />

      <PageContent $fullWidth>
        <Card $noPadding>
          <Tabs>
            {tabs.map((tab) => (
              <Tab
                key={tab.id}
                $active={activeTab === tab.id}
                onClick={(event) => handleTabChange(tab.id, event)}
              >
                {tab.label}
              </Tab>
            ))}
          </Tabs>

          <TabContent>{renderTabContent()}</TabContent>
        </Card>
      </PageContent>

      {/* Bot Selection Modal */}
      <Modal isOpen={!!botSelectionModal} onClose={handleCancelBotSelection}>
        <ModalTitle>Manage allowed bots</ModalTitle>
        <ModalDescription>
          Choose which bots <strong>{botSelectionModal?.username}</strong> can
          access.
        </ModalDescription>

        {loadingUserBots ? (
          <div style={{ textAlign: "center", padding: "2rem" }}>
            Loading user's allowed bots...
          </div>
        ) : (() => {
            return usernameRules.some(
              (rule) => rule.username === botSelectionModal?.username
            );
          })() ? (
          <div
            style={{
              textAlign: "center",
              padding: "2rem",
              backgroundColor: "#f8f9fa",
              borderRadius: "8px",
              margin: "1rem 0"
            }}
          >
            <h4 style={{ margin: "0 0 0.5rem 0", color: "#495057" }}>
              Controlled by username rule
            </h4>
            <p
              style={{
                margin: 0,
                color: "#6c757d",
                fontSize: "1rem"
              }}
            >
              This user's bot access is managed by a username-specific rule for{" "}
              <strong>{botSelectionModal?.username}</strong>.
            </p>
          </div>
        ) : (
          <>
            <ModeOption
              $isActive={isDefaultMode}
              onClick={() => {
                setIsDefaultMode(true);
                setIsDomainMode(false);
              }}
            >
              <ModeRadio
                type="radio"
                name="botMode"
                checked={isDefaultMode}
                onChange={() => {
                  setIsDefaultMode(true);
                  setIsDomainMode(false);
                }}
              />
              <ModeLabel>Default bots</ModeLabel>
            </ModeOption>

            {(() => {
              const userDomain = botSelectionModal?.username?.split("@")[1];
              const hasDomainRule = domainRules.some(
                (domain) => domain.domain === userDomain
              );
              return hasDomainRule;
            })() && (
              <ModeOption
                $isActive={isDomainMode}
                onClick={() => {
                  setIsDomainMode(true);
                  setIsDefaultMode(false);
                }}
              >
                <ModeRadio
                  type="radio"
                  name="botMode"
                  checked={isDomainMode}
                  onChange={() => {
                    setIsDomainMode(true);
                    setIsDefaultMode(false);
                  }}
                />
                <ModeLabel>
                  Bots for{" "}
                  <strong>{botSelectionModal?.username?.split("@")[1]}</strong>{" "}
                  users
                </ModeLabel>
              </ModeOption>
            )}

            <ModeOption
              $isActive={!isDomainMode && !isDefaultMode}
              onClick={() => {
                setIsDomainMode(false);
                setIsDefaultMode(false);
              }}
            >
              <ModeRadio
                type="radio"
                name="botMode"
                checked={!isDomainMode && !isDefaultMode}
                onChange={() => {
                  setIsDomainMode(false);
                  setIsDefaultMode(false);
                }}
              />
              <ModeLabel>Specific bots</ModeLabel>
            </ModeOption>

            {!isDomainMode && !isDefaultMode && (
              <BotList>
                {availableBots.map((bot) => (
                  <BotItem key={bot.id} onClick={() => handleBotToggle(bot.id)}>
                    <BotCheckbox
                      type="checkbox"
                      id={`bot-${bot.id}`}
                      checked={selectedBots.has(bot.id)}
                      onChange={() => {}}
                      disabled={updatingBots || loadingUserBots}
                    />
                    <BotLabel htmlFor={`bot-${bot.id}`}>
                      <BotIcon>{getBotIcon(bot)}</BotIcon>
                      {bot.name}
                    </BotLabel>
                  </BotItem>
                ))}
              </BotList>
            )}
          </>
        )}

        <ModalActions>
          <Button variant="secondary" onClick={handleCancelBotSelection}>
            {(() => {
              return usernameRules.some(
                (rule) => rule.username === botSelectionModal?.username
              );
            })()
              ? "Close"
              : "Cancel"}
          </Button>
          {!(() => {
            return usernameRules.some(
              (rule) => rule.username === botSelectionModal?.username
            );
          })() && (
            <Button
              onClick={handleSaveAllowedBots}
              disabled={updatingBots || loadingUserBots}
            >
              {updatingBots ? "Saving..." : "Save"}
            </Button>
          )}
        </ModalActions>
      </Modal>
    </>
  );
}

export default Admin;
